// Draggable Marquee Component
// Uses GSAP Observer for drag interactions and ScrollTrigger for viewport detection
import { gsap, ScrollTrigger } from '../gsap-config.js';

let Observer = null;
let observerImportPromise = null;

// Try to load Observer dynamically (it's a GSAP Club plugin, may not be available)
function ensureObserver() {
  if (Observer !== null) return Promise.resolve(Observer);
  if (observerImportPromise) return observerImportPromise;
  
  observerImportPromise = (async () => {
    try {
      const observerModule = await import('gsap/Observer');
      Observer = observerModule.Observer;
      return Observer;
    } catch (e) {
      Observer = null;
      return null;
    }
  })();
  
  return observerImportPromise;
}

// Start loading Observer immediately
ensureObserver();

let marqueeInstances = new WeakMap();
let refreshTimeout = null;

// Debounced ScrollTrigger refresh
function debouncedRefresh() {
  clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(() => {
    ScrollTrigger.refresh();
  }, 100);
}

async function initDraggableMarquee() {
  const wrappers = document.querySelectorAll("[data-draggable-marquee-init]");

  const getNumberAttr = (el, name, fallback) => {
    const value = parseFloat(el.getAttribute(name));
    return Number.isFinite(value) ? value : fallback;
  };

  for (const wrapper of wrappers) {
    if (wrapper.getAttribute("data-draggable-marquee-init") === "initialized") {
      return;
    }

    const collection = wrapper.querySelector("[data-draggable-marquee-collection]");
    const list = wrapper.querySelector("[data-draggable-marquee-list]");
    if (!collection || !list) return;

    const duration = getNumberAttr(wrapper, "data-duration", 20);
    const multiplier = getNumberAttr(wrapper, "data-multiplier", 40);
    const sensitivity = getNumberAttr(wrapper, "data-sensitivity", 0.01);

    const wrapperWidth = wrapper.getBoundingClientRect().width;
    const listWidth = list.scrollWidth || list.getBoundingClientRect().width;
    if (!wrapperWidth || !listWidth) {
      return;
    }

    // Make enough duplicates to cover screen - need at least 2 copies for seamless loop
    // Calculate how many clones we need based on listWidth
    const minRequiredWidth = wrapperWidth + listWidth + 2;
    const minClonesNeeded = Math.ceil(minRequiredWidth / listWidth);
    
    try {
      // Create clones based on calculated need, not scrollWidth (which may not update immediately)
      // Ensure we have at least 2 copies for seamless looping
      const clonesToCreate = Math.max(minClonesNeeded, 2);
      
      for (let i = 0; i < clonesToCreate; i++) {
        const listClone = list.cloneNode(true);
        listClone.setAttribute("data-draggable-marquee-clone", "");
        listClone.setAttribute("aria-hidden", "true");
        
        try {
          collection.appendChild(listClone);
        } catch (appendError) {
          throw appendError;
        }
      }
      
      // Force a layout recalculation after all clones are added
      void collection.offsetHeight;
    } catch (e) {
      console.error('List cloning error:', e);
      throw e;
    }

    const wrapX = gsap.utils.wrap(-listWidth, 0);
    
    try {
      gsap.set(collection, { x: 0 });
    } catch (e) {
      throw e;
    }
    
    let marqueeLoop = null;
    try {
      marqueeLoop = gsap.to(collection, {
        x: -listWidth,
        duration,
        ease: "none",
        repeat: -1,
        onReverseComplete: () => marqueeLoop.progress(1),
        modifiers: {
          x: (x) => wrapX(parseFloat(x)) + "px"
        },
      });
    } catch (e) {
      throw e;
    }
    
    // Direction can be used for css + set initial direction on load
    const initialDirectionAttr = (wrapper.getAttribute("data-direction") || "left").toLowerCase();
    const baseDirection = initialDirectionAttr === "right" ? -1 : 1;
    
    const timeScale = { value: 1 };
    
    timeScale.value = baseDirection;
    wrapper.setAttribute("data-direction", baseDirection < 0 ? "right" : "left");
    
    if (baseDirection < 0) marqueeLoop.progress(1);
    
    function applyTimeScale() {
      marqueeLoop.timeScale(timeScale.value);
      wrapper.setAttribute("data-direction", timeScale.value < 0 ? "right" : "left");
    }
    
    applyTimeScale();

    // Drag observer - wait for Observer to be available
    let marqueeObserver = null;
    
    // Wait for Observer to be available if import is still pending
    const currentObserver = Observer || await ensureObserver();
    
    if (currentObserver && typeof currentObserver.create === 'function') {
      try {
        marqueeObserver = currentObserver.create({
          target: wrapper,
          type: "pointer,touch",
          preventDefault: true,
          debounce: false,
          onChangeX: (observerEvent) => {
            let velocityTimeScale = observerEvent.velocityX * -sensitivity;
            velocityTimeScale = gsap.utils.clamp(-multiplier, multiplier, velocityTimeScale);

            gsap.killTweensOf(timeScale);

            const restingDirection = velocityTimeScale < 0 ? -1 : 1;

            gsap.timeline({ onUpdate: applyTimeScale })
              .to(timeScale, { value: velocityTimeScale, duration: 0.1, overwrite: true })
              .to(timeScale, { value: restingDirection, duration: 1.0 });
          }
        });
      } catch (e) {
        marqueeObserver = null;
      }
    }

    // Pause marquee when scrolled out of view
    let scrollTrigger = null;
    try {
      scrollTrigger = ScrollTrigger.create({
        trigger: wrapper,
        start: "top bottom",
        end: "bottom top",
        onEnter: () => { 
          try {
            marqueeLoop.resume(); 
            applyTimeScale(); 
            if (marqueeObserver && typeof marqueeObserver.enable === 'function') {
              marqueeObserver.enable(); 
            }
          } catch (e) {
            console.error('ScrollTrigger onEnter error:', e);
          }
        },
        onEnterBack: () => { 
          try {
            marqueeLoop.resume(); 
            applyTimeScale(); 
            if (marqueeObserver && typeof marqueeObserver.enable === 'function') {
              marqueeObserver.enable(); 
            }
          } catch (e) {
            console.error('ScrollTrigger onEnterBack error:', e);
          }
        },
        onLeave: () => { 
          try {
            marqueeLoop.pause(); 
            if (marqueeObserver && typeof marqueeObserver.disable === 'function') {
              marqueeObserver.disable(); 
            }
          } catch (e) {
            console.error('ScrollTrigger onLeave error:', e);
          }
        },
        onLeaveBack: () => { 
          try {
            marqueeLoop.pause(); 
            if (marqueeObserver && typeof marqueeObserver.disable === 'function') {
              marqueeObserver.disable(); 
            }
          } catch (e) {
            console.error('ScrollTrigger onLeaveBack error:', e);
          }
        }
      });
    } catch (e) {
      // ScrollTrigger creation failed, continue without viewport pausing
    }

    // Store instance for cleanup
    marqueeInstances.set(wrapper, {
      marqueeLoop,
      marqueeObserver,
      scrollTrigger
    });
    
    wrapper.setAttribute("data-draggable-marquee-init", "initialized");
  }
  
  // Refresh ScrollTrigger after all marquees are initialized
  // This ensures other sections' ScrollTriggers can properly calculate their positions
  // after the marquee DOM changes (cloned elements)
  // Use debounced refresh to avoid performance issues
  debouncedRefresh();
}

// Cleanup function for island pattern
function cleanupDraggableMarquee(wrapper) {
  const instance = marqueeInstances.get(wrapper);
  if (instance) {
    if (instance.marqueeLoop) instance.marqueeLoop.kill();
    if (instance.marqueeObserver) instance.marqueeObserver.kill();
    if (instance.scrollTrigger) instance.scrollTrigger.kill();
    marqueeInstances.delete(wrapper);
  }
}

// Initialize when script loads (for island pattern)
// Use requestAnimationFrame to ensure DOM is ready
// The idempotent check in initDraggableMarquee prevents double initialization
requestAnimationFrame(async () => {
  try {
    await initDraggableMarquee();
  } catch (e) {
    console.error('Draggable marquee initialization error:', e);
  }
});

// Also initialize on DOM ready (for non-island usage)
// Only if document is still loading (prevents double initialization)
if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", () => {
    requestAnimationFrame(async () => {
      try {
        await initDraggableMarquee();
      } catch (e) {
        console.error('Draggable marquee initialization error:', e);
      }
    });
  });
}

export { initDraggableMarquee, cleanupDraggableMarquee };
